/*
  dylaras-wishlist.js
  DYLARAS heart buttons, saved to the Swym Wishlist app (synced to the shopper's account and
  available on the Swym page). If Swym doesn't load, favorites fall back to the browser
  (localStorage) so the heart always works.
  - [data-dylaras-wishlist-toggle]  heart buttons on cards and the product page
  - [data-dylaras-wishlist-count]   header counter
  - [data-dylaras-wishlist-page]    renders browser-saved pieces on /pages/wishlist (fallback only)
*/
(() => {
  const KEY = 'dylaras-wishlist';
  const isSpanish = (document.documentElement.lang || '').toLowerCase().startsWith('es');
  const TEXT = isSpanish
    ? { saved: 'Guardado en tus favoritos', removed: 'Quitado de tus favoritos', view: 'Ver favoritos', error: 'No pudimos guardar. Inténtalo de nuevo.' }
    : { saved: 'Saved to your favorites', removed: 'Removed from your favorites', view: 'View favorites', error: "We couldn't save that. Please try again." };

  let mode = 'local'; // 'swym' once the app is ready
  let swat = null;
  let listId = null;
  /** product ids (Swym) or handles (browser) */
  let saved = new Set();
  /** full Swym list items, used to draw the favorites page */
  let swymItems = [];

  /* ── Browser storage (fallback) ── */
  function localRead() {
    try {
      const value = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(value) ? value : [];
    } catch {
      return [];
    }
  }

  function localWrite(handles) {
    try {
      localStorage.setItem(KEY, JSON.stringify(handles));
    } catch {
      /* storage blocked (private mode): the list simply won't persist */
    }
  }

  function keyFor(button) {
    return mode === 'swym' ? String(button.dataset.productId || '') : button.dataset.handle;
  }

  function refresh() {
    document.querySelectorAll('[data-dylaras-wishlist-toggle]').forEach((button) => {
      const isSaved = saved.has(keyFor(button));
      button.setAttribute('aria-pressed', String(isSaved));
      const label = isSaved ? button.dataset.labelRemove : button.dataset.labelAdd;
      if (label) button.setAttribute('aria-label', label);
      const text = button.querySelector('[data-dylaras-wishlist-text]');
      // Only touch the text when it changes: rewriting it fires the MutationObserver below again
      if (text && label && text.textContent !== label) text.textContent = label;
    });
    document.querySelectorAll('[data-dylaras-wishlist-count]').forEach((count) => {
      const value = String(saved.size);
      if (count.textContent !== value) count.textContent = value;
      count.hidden = saved.size === 0;
    });
  }

  /* ── Small confirmation with a link to the favorites page ── */
  function wishlistUrl() {
    const link = document.querySelector('.dylaras-wishlist-link');
    return link ? link.getAttribute('href') : '/pages/wishlist';
  }

  let toastTimer = 0;
  function toast(message, withLink) {
    let box = document.querySelector('.dylaras-wishlist-toast');
    if (!box) {
      box = document.createElement('div');
      box.className = 'dylaras-wishlist-toast';
      box.setAttribute('role', 'status');
      box.setAttribute('aria-live', 'polite');
      document.body.appendChild(box);
    }
    box.textContent = message;
    if (withLink) {
      const link = document.createElement('a');
      link.href = wishlistUrl();
      link.textContent = TEXT.view;
      box.appendChild(link);
    }
    box.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => box.classList.remove('is-visible'), 4000);
  }

  /* ── Swym ── */
  function swymProduct(button) {
    return {
      epi: Number(button.dataset.variantId),
      empi: Number(button.dataset.productId),
      du: button.dataset.url,
      iu: button.dataset.image,
      pr: Number(button.dataset.price) || 0,
      dt: button.dataset.title,
    };
  }

  function setFromItems(items) {
    swymItems = items || [];
    saved = new Set(swymItems.map((item) => String(item.empi)));
    refresh();
    renderPage();
  }

  function reloadSwymItems() {
    if (listId && swat.api && swat.api.fetchListCtx) {
      swat.api.fetchListCtx({ lid: listId }, setFromItems, () => {});
    } else if (swat.fetch) {
      swat.fetch(setFromItems);
    }
  }

  function loadSwymList() {
    const api = swat.api;
    api.fetchLists({
      callbackFn: (lists) => {
        if (lists && lists.length) {
          listId = lists[0].lid;
          api.fetchListCtx({ lid: listId }, setFromItems, () => setFromItems([]));
        } else {
          setFromItems([]);
        }
      },
      errorFn: () => setFromItems([]),
    });
  }

  function ensureList(done, fail) {
    if (listId) return done(listId);
    swat.api.createList(
      { lname: 'My Favorites' },
      (list) => {
        listId = list.lid;
        done(listId);
      },
      fail
    );
  }

  function swymToggle(button, wasSaved) {
    const product = swymProduct(button);
    const fail = () => {
      // Undo the optimistic change
      if (wasSaved) saved.add(String(product.empi));
      else saved.delete(String(product.empi));
      refresh();
      toast(TEXT.error, false);
    };

    if (swat.api && swat.api.addToList) {
      ensureList((lid) => {
        const action = wasSaved ? swat.api.deleteFromList : swat.api.addToList;
        action.call(
          swat.api,
          lid,
          product,
          () => {
            toast(wasSaved ? TEXT.removed : TEXT.saved, !wasSaved);
            reloadSwymItems();
          },
          fail
        );
      }, fail);
    } else if (swat.addToWishList) {
      // Older Swym API
      const action = wasSaved ? swat.removeFromWishList : swat.addToWishList;
      action.call(swat, product, () => {
        toast(wasSaved ? TEXT.removed : TEXT.saved, !wasSaved);
        reloadSwymItems();
      });
    } else {
      fail();
    }
  }

  function startSwym(instance) {
    if (mode === 'swym' || !instance) return;
    swat = instance;
    mode = 'swym';
    if (swat.api && swat.api.fetchLists) {
      loadSwymList();
    } else if (swat.fetch) {
      swat.fetch(setFromItems);
    } else {
      setFromItems([]);
    }
  }

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(startSwym);
  if (window._swat) startSwym(window._swat);

  /* ── Clicks ── */
  // Heart buttons sit inside product cards (which are links): don't navigate when toggling
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-dylaras-wishlist-toggle]') : null;
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();

      const key = keyFor(button);
      const wasSaved = saved.has(key);
      if (wasSaved) saved.delete(key);
      else saved.add(key);
      refresh();

      if (mode === 'swym') {
        swymToggle(button, wasSaved);
      } else {
        const handles = localRead().filter((handle) => handle !== key);
        if (!wasSaved) handles.unshift(key);
        localWrite(handles);
        toast(wasSaved ? TEXT.removed : TEXT.saved, !wasSaved);
        if (document.querySelector('[data-dylaras-wishlist-page]')) renderPage();
      }
    },
    true
  );

  // Keep several tabs in sync (browser mode)
  window.addEventListener('storage', (event) => {
    if (event.key === KEY && mode === 'local') {
      saved = new Set(localRead());
      refresh();
    }
  });

  /* ── Browser-saved favorites page (fallback) ── */
  function money(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(
      cents / 100
    );
  }

  async function renderPage() {
    const page = document.querySelector('[data-dylaras-wishlist-page]');
    if (!page) return;
    const grid = page.querySelector('[data-dylaras-wishlist-grid]');
    const empty = page.querySelector('[data-dylaras-wishlist-empty]');
    if (mode === 'swym') return renderSwymPage(page, grid, empty);

    const handles = localRead();

    empty.hidden = handles.length > 0;
    if (handles.length === 0) {
      grid.innerHTML = '';
      return;
    }

    const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    const products = await Promise.all(
      handles.map((handle) =>
        fetch(`${root}products/${encodeURIComponent(handle)}.js`)
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      )
    );

    // Drop products that no longer exist
    const found = products.filter(Boolean);
    if (found.length !== handles.length) localWrite(found.map((product) => product.handle));

    const soldOut = page.dataset.labelSoldOut;
    const removeLabel = page.dataset.labelRemove;
    grid.innerHTML = found
      .map((product) => {
        const src = product.featured_image || '';
        const sized = src ? `${src}${src.includes('?') ? '&' : '?'}width=600` : '';
        const image = sized ? `<img src="${sized}" alt="" loading="lazy" class="dylaras-featured__img">` : '';
        const compare =
          product.compare_at_price > product.price
            ? `<s class="dylaras-featured__price--compare">${money(product.compare_at_price)}</s> `
            : '';
        return `
          <a href="${product.url}" class="dylaras-featured__card dylaras-wishlist-card">
            <div class="dylaras-featured__img-wrap">
              ${image}
              <button type="button" class="dylaras-wishlist-btn dylaras-wishlist-btn--card"
                data-dylaras-wishlist-toggle data-handle="${product.handle}" data-product-id="${product.id}"
                aria-pressed="true" aria-label="${removeLabel}">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20.5s-7.5-4.6-7.5-10.3A4.2 4.2 0 0 1 12 7.6a4.2 4.2 0 0 1 7.5 2.6c0 5.7-7.5 10.3-7.5 10.3z"/></svg>
              </button>
            </div>
            <div class="dylaras-featured__info">
              <h3 class="dylaras-featured__title"></h3>
              <p class="dylaras-featured__price">${compare}${money(product.price)}${
                product.available ? '' : ` · <span class="dylaras-wishlist-card__sold">${soldOut}</span>`
              }</p>
            </div>
          </a>`;
      })
      .join('');

    // Titles as text (never HTML) to stay safe
    grid.querySelectorAll('.dylaras-wishlist-card').forEach((card, index) => {
      card.querySelector('.dylaras-featured__title').textContent = found[index].title;
    });
    refresh();
  }

  /** Favorites page from the Swym list (items carry title, image, price and URL) */
  function renderSwymPage(page, grid, empty) {
    empty.hidden = swymItems.length > 0;
    const removeLabel = page.dataset.labelRemove;
    const heart =
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20.5s-7.5-4.6-7.5-10.3A4.2 4.2 0 0 1 12 7.6a4.2 4.2 0 0 1 7.5 2.6c0 5.7-7.5 10.3-7.5 10.3z"/></svg>';

    grid.innerHTML = '';
    swymItems.forEach((item) => {
      // Built with DOM properties (never HTML strings) so list data stays safe
      const card = document.createElement('a');
      card.className = 'dylaras-featured__card dylaras-wishlist-card';
      card.href = item.du || '#';

      const wrap = document.createElement('div');
      wrap.className = 'dylaras-featured__img-wrap';
      if (item.iu) {
        const img = document.createElement('img');
        img.src = item.iu;
        img.alt = '';
        img.loading = 'lazy';
        img.className = 'dylaras-featured__img';
        wrap.appendChild(img);
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'dylaras-wishlist-btn dylaras-wishlist-btn--card';
      button.setAttribute('data-dylaras-wishlist-toggle', '');
      button.setAttribute('aria-pressed', 'true');
      button.setAttribute('aria-label', removeLabel);
      button.dataset.productId = String(item.empi);
      button.dataset.variantId = String(item.epi);
      button.dataset.url = item.du || '';
      button.dataset.title = item.dt || '';
      button.dataset.image = item.iu || '';
      button.dataset.price = String(item.pr || '');
      button.dataset.labelRemove = removeLabel;
      button.dataset.labelAdd = removeLabel;
      button.innerHTML = heart;
      wrap.appendChild(button);

      const info = document.createElement('div');
      info.className = 'dylaras-featured__info';
      const title = document.createElement('h3');
      title.className = 'dylaras-featured__title';
      title.textContent = item.dt || '';
      const price = document.createElement('p');
      price.className = 'dylaras-featured__price';
      price.textContent = item.pr ? money(Math.round(Number(item.pr) * 100)) : '';
      info.append(title, price);

      card.append(wrap, info);
      grid.appendChild(card);
    });
    refresh();
  }

  function init() {
    if (mode === 'local') saved = new Set(localRead());
    refresh();
    if (mode === 'local') renderPage();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Product sections re-render on variant change / infinite scroll: re-sync hearts
  // (our own updates are not observed, otherwise the page never goes idle and the
  // collection sort, grid and filters, which wait for idle time, stop responding)
  let pending = 0;
  const observer = new MutationObserver(() => {
    cancelAnimationFrame(pending);
    pending = requestAnimationFrame(() => {
      observer.disconnect();
      refresh();
      observer.takeRecords();
      observer.observe(document.body, { childList: true, subtree: true });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();
